import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import TeacherEditModal from "./TeacherEditModal";
import TeacherRegisterModal from "./TeacherRegisterModal";
import TeacherViewModal from "./TeacherViewModal";

function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [viewTeacher, setViewTeacher] = useState(null);
  const [editTeacher, setEditTeacher] = useState(null);
  const [createdTeacher, setCreatedTeacher] = useState(null);
  const [resetDetails, setResetDetails] = useState(null);

  const filteredTeachers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return teachers;
    return teachers.filter((teacher) => {
      return (
        teacher.full_name?.toLowerCase().includes(term) ||
        teacher.staff_number?.toLowerCase().includes(term) ||
        teacher.email?.toLowerCase().includes(term) ||
        teacher.department?.toLowerCase().includes(term) ||
        teacher.job_title?.toLowerCase().includes(term)
      );
    });
  }, [teachers, searchTerm]);

  const activeCount = teachers.filter((teacher) => teacher.is_active).length;
  const pendingCount = teachers.filter((teacher) => teacher.status === "PENDING").length;
  const departmentsCount = new Set(teachers.map((teacher) => teacher.department).filter(Boolean)).size;

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("teachers/");
      setTeachers(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load teachers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const handleTeacherCreated = (teacher) => {
    setCreatedTeacher(teacher);
    setShowRegisterModal(false);
    fetchTeachers();
  };

  const handleTeacherUpdated = (teacher) => {
    setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? teacher : item)));
    setEditTeacher(null);
    setViewTeacher(null);
  };

  const resetTeacherPassword = async (teacher) => {
    if (!window.confirm(`Reset password for ${teacher.full_name}?`)) return;
    try {
      const response = await axiosInstance.post(`teachers/${teacher.id}/reset-password/`);
      const data = response.data;
      setTeachers((prev) => prev.map((item) => (item.id === data.teacher.id ? data.teacher : item)));
      setResetDetails({
        full_name: data.teacher.full_name,
        email: data.email,
        username: data.username,
        temporary_password: data.temporary_password,
      });
      toast.success("Teacher password reset successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reset teacher password.");
    }
  };

  const approveTeacher = async (teacher) => {
    try {
      const response = await axiosInstance.post(`teachers/${teacher.id}/approve/`);
      handleTeacherUpdated(response.data.teacher);
      toast.success("Teacher approved successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to approve teacher.");
    }
  };

  const copyCreatedTeacher = async () => {
    if (!createdTeacher) return;
    const text = `Teacher: ${createdTeacher.full_name}
Email: ${createdTeacher.email}
Username: ${createdTeacher.username}
Temporary Password: ${createdTeacher.temporary_password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Teacher login details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  const copyResetDetails = async () => {
    if (!resetDetails) return;
    const text = `Teacher: ${resetDetails.full_name}
Email: ${resetDetails.email}
Username: ${resetDetails.username}
Temporary Password: ${resetDetails.temporary_password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("New password details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>Teachers</h3>
          <p className="text-muted mb-0">
            Manage teacher records, departments, subjects, classes, and staff access.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-light border" onClick={fetchTeachers} title="Refresh teachers">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button className="btn btn-success" onClick={() => setShowRegisterModal(true)}>
            <i className="bi bi-person-plus me-2"></i>
            Add Teacher
          </button>
        </div>
      </div>

      {createdTeacher && (
        <div className="alert alert-success border-0 shadow-sm mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h6 className="fw-bold mb-1">Teacher registered successfully</h6>
              <div><strong>{createdTeacher.full_name}</strong> can login using email or username.</div>
              <small className="text-muted">Temporary password: {createdTeacher.temporary_password}</small>
            </div>
            <button className="btn btn-success btn-sm" onClick={copyCreatedTeacher}>
              <i className="bi bi-clipboard-check me-2"></i>
              Copy Details
            </button>
          </div>
        </div>
      )}

      {resetDetails && (
        <div className="alert alert-warning border-0 shadow-sm mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h6 className="fw-bold mb-1">Teacher password reset</h6>
              <div><strong>{resetDetails.full_name}</strong> can login using email or username.</div>
              <small className="text-muted">New temporary password: {resetDetails.temporary_password}</small>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-warning btn-sm" onClick={copyResetDetails}>
                <i className="bi bi-clipboard-check me-2"></i>
                Copy Details
              </button>
              <button className="btn btn-light btn-sm" onClick={() => setResetDetails(null)}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Total Teachers</small><h3 className="fw-bold mb-0">{teachers.length}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-person-workspace fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Active</small><h3 className="fw-bold mb-0">{activeCount}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-check2-circle fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Pending</small><h3 className="fw-bold mb-0">{pendingCount}</h3></div><div className="rounded-3 p-3 text-warning" style={{ background: "#fff8e6" }}><i className="bi bi-hourglass-split fs-3"></i></div></div></div></div>
        <div className="col-md-3"><div className="card border-0 shadow-sm h-100"><div className="card-body d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">Departments</small><h3 className="fw-bold mb-0">{departmentsCount}</h3></div><div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}><i className="bi bi-diagram-3 fs-3"></i></div></div></div></div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Teacher Records</h5>
            <input type="search" className="form-control" style={{ maxWidth: 320 }} placeholder="Search name, staff no, department..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4 text-center text-muted">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading teachers...
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Teacher</th>
                    <th>Employee Number</th>
                    <th>Department</th>
                    <th>Subjects</th>
                    <th>Classes</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id}>
                      <td><div className="fw-bold">{teacher.full_name}</div><small className="text-muted">{teacher.email}</small></td>
                      <td><span className="badge text-bg-success">{teacher.staff_number}</span></td>
                      <td><div>{teacher.department || "Not set"}</div><small className="text-muted">{teacher.job_title || ""}</small></td>
                      <td>{teacher.subjects_taught?.length ? teacher.subjects_taught.join(", ") : "Not assigned"}</td>
                      <td>{teacher.classes_assigned?.length ? teacher.classes_assigned.join(", ") : "Not assigned"}</td>
                      <td><span className={`badge ${teacher.is_active ? "text-bg-success" : "text-bg-secondary"}`}>{teacher.is_active ? teacher.status || "ACTIVE" : "INACTIVE"}</span></td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-light border me-2" title="View teacher" onClick={() => setViewTeacher(teacher)}><i className="bi bi-eye text-success"></i></button>
                        <button className="btn btn-sm btn-light border me-2" title="Edit teacher" onClick={() => setEditTeacher(teacher)}><i className="bi bi-pencil-square text-success"></i></button>
                        <button className="btn btn-sm btn-light border me-2" title="Reset password" onClick={() => resetTeacherPassword(teacher)}><i className="bi bi-key text-warning"></i></button>
                        {teacher.status === "PENDING" && (
                          <button className="btn btn-sm btn-light border" title="Approve teacher" onClick={() => approveTeacher(teacher)}><i className="bi bi-check2-circle text-success"></i></button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredTeachers.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-muted py-4">No teachers found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <TeacherRegisterModal show={showRegisterModal} onClose={() => setShowRegisterModal(false)} onCreated={handleTeacherCreated} />
      <TeacherViewModal teacher={viewTeacher} onClose={() => setViewTeacher(null)} onEdit={(teacher) => { setViewTeacher(null); setEditTeacher(teacher); }} />
      <TeacherEditModal teacher={editTeacher} onClose={() => setEditTeacher(null)} onUpdated={handleTeacherUpdated} />
    </div>
  );
}

export default Teachers;
